// Транспорт: базовый адрес, токен, разбор ошибок.
//
// Платформенного кода здесь нет и быть не должно — `fetch` есть и в браузере,
// и в React Native, и в Node 22. Хранилище токена платформенное, поэтому оно
// не встроено, а передаётся снаружи (см. TokenStore).

import { CAPS_HEADER, PLATFORM_HEADER, type ClientCap, type ClientPlatform } from '@smyslika/core'

/** Ошибка от сервера: код и разобранное тело, а не строка. */
export class ApiError extends Error {
  readonly status: number
  /** Машинная причина, если сервер её назвал: `exhausted`, `not_in_tariff`, … */
  readonly reason: string | undefined
  /** Тело ответа целиком: у 402 там ещё остаток, лимит и дата обновления. */
  readonly body: Record<string, unknown>
  /**
   * Через сколько секунд повторять — из заголовка `retry-after`.
   *
   * Сервер шлёт его затем, чтобы экран назвал точный срок вместо
   * «попробуйте позже» (комментарий у ручки запроса кода). Не прочитать его
   * значит показать своё, пессимистичное число: у человека кончилась
   * половина паузы, а мы попросим ждать полную.
   */
  readonly retryAfterSeconds: number | undefined

  constructor(status: number, body: Record<string, unknown>, retryAfterSeconds?: number) {
    const message = typeof body.error === 'string' ? body.error : `Ошибка ${status}`
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.reason = typeof body.reason === 'string' ? body.reason : undefined
    this.body = body
    this.retryAfterSeconds = retryAfterSeconds
  }
}

/**
 * Сеть не ответила вовсе. Отличать от ApiError обязательно: у первой сервер
 * сказал «нет», у второй мы не знаем, дошёл запрос или нет, — и повторять
 * можно только то, что безопасно повторить.
 */
export class OfflineError extends Error {
  constructor(cause?: unknown) {
    super('Нет связи с сервером')
    this.name = 'OfflineError'
    this.cause = cause
  }
}

/**
 * Отказ без нашего текста — словами человека. SMY-353, 05.10.2026
 *
 * Наш api отказывает своим текстом (`{ error, reason }`). Остальное приходит с
 * дороги или не от него: 502 от прокси с пустым телом, пока api перезапускается
 * при выкладке, страница ошибки не в JSON, отказ самого Fastify с английской
 * фразой («Bad Request», «Payload Too Large»). До этого человек читал «Ошибка
 * 500» — и не знал, повторять ли, — либо первые двести знаков чужой страницы.
 *
 * 502 НЕ превращается в `OfflineError`: сеть была, ответил сервер, и смешанные,
 * они молча повторяли бы платный запрос (`ARCHITECTURE.md`, «Форма ошибки»).
 * Тексты — `docs/copy/chat-recovery.md`.
 */
export const SERVER_UNAVAILABLE = 'Сервер не ответил. Попробуй ещё раз'

/**
 * Запрос не дождался ответа за свой срок (`ATTEMPT_WAIT_MS` в `attempts.ts`):
 * ушёл, а ответа нет — то есть «сервер не ответил». SMY-449
 */
export class AttemptTimeout extends Error {
  constructor() {
    super(SERVER_UNAVAILABLE)
    this.name = 'AttemptTimeout'
  }
}

/** Почему исход неизвестен: оборвалась связь или не ответил сервер. */
export type AttemptCause = 'offline' | 'server'

/**
 * Исход запроса неизвестен — и почему: дошёл ли он до api, сказать нельзя.
 * SMY-449
 *
 * Обрыв связи (`OfflineError`) — «нет связи»; 5xx без нашего текста — прокси,
 * принявший запрос и не дождавшийся ответа (502 от Caddy, пока api
 * перезапускается при выкладке), — и свой срок (`AttemptTimeout`) — «сервер не
 * ответил». Строки у поводов свои (решение владельца 06.10.2026, «своя
 * строка»). `null` — исход известен: наш отказ или своя отмена (`AbortError`).
 *
 * Молча повторить такой запрос можно ТОЛЬКО с ключом попытки — тем же, что у
 * первой: по нему сервер отдаст прежний запуск. Без ключа повтор — второй
 * запуск, и тогда эти исходы смешивать нельзя (шапка выше). Одно правило на
 * оба клиента: разойдись копии, один дослал бы, а другой показал бы отказ — и
 * человек нажал бы снова, уже с новым ключом (ворота ревью 06.10.2026).
 */
export function outcomeCause(e: unknown): AttemptCause | null {
  if (e instanceof AttemptTimeout) return 'server'
  if (e instanceof OfflineError) return 'offline'
  if (e instanceof ApiError && e.message === SERVER_UNAVAILABLE) return 'server'
  return null
}

/** 413 без нашего текста — тело больше предела Fastify. SMY-353 */
export const REQUEST_TOO_LARGE = 'Текст слишком длинный — столько за раз не принимаю. Сократи его и отправь ещё раз'

/**
 * Прочий 4xx без нашего текста. SMY-353
 *
 * Чаще всего это старый клиент: вкладка, открытая до выкладки, ходит по
 * адресам, которых у сервера уже нет. Повтор тем же клиентом исход не меняет —
 * помогает обновить его, и шаг поэтому у платформ свой.
 */
export const REQUEST_REJECTED = {
  web: 'Сервер не принял запрос: страница могла устареть. Обнови её и попробуй ещё раз',
  app: 'Сервер не принял запрос: приложение могло устареть. Обнови его и попробуй ещё раз',
} as const

/**
 * Тело отказа: наш текст в `error`, если он там есть, иначе `fallback`. SMY-353
 *
 * Наш текст — строка `error` в JSON без полей умолчания Fastify: у того в теле
 * есть числовой `statusCode` и `message`, а наши ручки так не отвечают. Одна
 * функция на три дороги — `request` и загрузки файла и фотографии, которые идут
 * мимо него (multipart): до этого каждая разбирала тело своей копией, и
 * не-JSON страница становилась текстом ошибки во всех трёх.
 */
export function failureBody(text: string, fallback: string): Record<string, unknown> {
  let body: Record<string, unknown> = {}
  if (text !== '') {
    try {
      const parsed: unknown = JSON.parse(text)
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) body = parsed as Record<string, unknown>
    } catch {
      // Не JSON — ответили не мы; страницу прокси человеку не показываем.
    }
  }
  const fastify = typeof body.statusCode === 'number' && typeof body.message === 'string'
  return typeof body.error === 'string' && !fastify ? body : { ...body, error: fallback }
}

/** Чем объяснить отказ без нашего текста — по классу кода. SMY-353 */
const fallbackFor = (status: number, rejected: string): string =>
  status >= 500 ? SERVER_UNAVAILABLE : status === 413 ? REQUEST_TOO_LARGE : rejected

/**
 * Где лежит токен. В вебе это localStorage, в React Native — SecureStore;
 * знать об этом клиенту незачем.
 */
export interface TokenStore {
  get(): string | null
  set(token: string | null): void
}

export interface ClientOptions {
  /** Базовый адрес API. В вебе обычно '' — тот же хост через прокси. */
  baseUrl: string
  tokens: TokenStore
  /**
   * Вызывается, когда сервер отверг ТЕКУЩИЙ токен: сессия кончилась. Токен к
   * этому моменту уже стёрт. Отказ запросу без токена или с прежним токеном
   * сюда не приходит — см. разбор 401 в `request`.
   */
  onUnauthorized?: () => void
  /**
   * Что сказать на 4xx без нашего текста — `REQUEST_REJECTED` своей платформы
   * (SMY-353). Шаг у платформ разный: обновить страницу или приложение.
   * Не задан — текст кабинета.
   */
  rejected?: string
  /**
   * Платформа сборки (SMY-494): уходит заголовком `PLATFORM_HEADER` на каждом
   * запросе. По ней сервер выключает типы задач решения 18 на iOS; без неё
   * считает клиента iOS. Задаёт сборка, а не экран.
   */
  platform?: ClientPlatform
  /**
   * Что сборка умеет рисовать сверх текста (SMY-557): уходит заголовком
   * `CAPS_HEADER` на каждом запросе, как платформа. По `cards` сервер включает
   * `where_to_buy` на iOS и отдаёт карточки товаров. Задаёт сборка, которая их
   * рисует, а не экран; не задано или пусто — заголовка нет, ответ прежний.
   */
  caps?: readonly ClientCap[]
  fetch?: typeof globalThis.fetch
}

export interface RequestOptions {
  // PATCH нужен там, где меняется ЧАСТЬ состояния: закрепить чат — это не
  // «переслать чат целиком», и PUT обещал бы серверу полную замену.
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Запрос без токена: вход и подтверждение кода. */
  anonymous?: boolean
}

export interface Client {
  readonly baseUrl: string
  readonly tokens: TokenStore
  request<T>(path: string, options?: RequestOptions): Promise<T>
  /**
   * Время сервера, а не устройства.
   *
   * Нужно там, где от времени зависят деньги: окно бесплатной правки длится
   * десять минут, и решает его сервер по своим часам (`isEditFree` в ядре).
   * Показать по часам устройства «ещё 9 минут», когда окно уже закрыто, —
   * это не неточность, а обещание, которое не выполнится.
   *
   * Смещение берётся из заголовка `date` последнего ответа. Пока ответов не
   * было, возвращает местное время: врать неоткуда, но и уточнять нечем.
   */
  serverNow(): Date
}

export function createClient(options: ClientOptions): Client {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis)

  /** Насколько часы устройства отстают от серверных, в миллисекундах. */
  let skewMs = 0

  async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = {}
    if (opts.body !== undefined) headers['content-type'] = 'application/json'
    /** С каким токеном ушёл запрос — к нему, и только к нему, относится 401. */
    const sent = opts.anonymous ? null : options.tokens.get()
    if (sent != null) headers.authorization = `Bearer ${sent}`
    if (options.platform != null) headers[PLATFORM_HEADER] = options.platform
    if (options.caps != null && options.caps.length > 0) headers[CAPS_HEADER] = options.caps.join(',')

    let res: Response
    try {
      res = await doFetch(options.baseUrl + path, {
        method: opts.method ?? (opts.body === undefined ? 'GET' : 'POST'),
        headers,
        ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
        ...(opts.signal ? { signal: opts.signal } : {}),
      })
    } catch (cause) {
      // AbortError — это наш собственный отказ, а не пропавшая сеть.
      if (cause instanceof Error && cause.name === 'AbortError') throw cause
      throw new OfflineError(cause)
    }

    // Заголовок `date` есть у каждого ответа: сервер обязан его слать.
    // Точность до секунды — больше и не нужно, окно правки в минутах.
    const stamp = res.headers.get('date')
    if (stamp != null) {
      const at = Date.parse(stamp)
      if (Number.isFinite(at)) skewMs = at - Date.now()
    }

    if (res.status === 204) return undefined as T

    // Тело читается отдельно от заголовков, и связь может оборваться между
    // ними. Это тот же обрыв, что выше, а не отказ: без этой обёртки наружу
    // уходил сырой `TypeError` браузера по-английски, а оборванный старт
    // запуска не узнавался как «исход неизвестен» (SMY-352).
    let text: string
    try {
      text = await res.text()
    } catch (cause) {
      if (cause instanceof Error && cause.name === 'AbortError') throw cause
      throw new OfflineError(cause)
    }

    if (!res.ok) {
      /**
       * 401 гасит сессию, только если отвергнут токен, который всё ещё текущий.
       * SMY-357, 05.10.2026
       *
       * До этого любой 401 стирал любой токен, и это било дважды:
       *
       * * вход по паролю идёт без токена (`anonymous`), а неверный пароль
       *   отвечает 401 — опечатка в пароле стирала гостю его сеанс;
       * * лента, список, остатки и `/v1/me` уходят разом. Сеанс, заменённый,
       *   пока они летели (новый гость, вход), стирался поздним 401 на прежний
       *   токен — и человек оставался совсем без сеанса.
       *
       * Сам отказ доходит до вызывающего в обоих случаях: запрос не выполнен.
       */
      if (res.status === 401 && sent != null && options.tokens.get() === sent) {
        options.tokens.set(null)
        options.onUnauthorized?.()
      }
      const retryAfter = Number(res.headers.get('retry-after'))
      throw new ApiError(
        res.status,
        failureBody(text, fallbackFor(res.status, options.rejected ?? REQUEST_REJECTED.web)),
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
      )
    }

    // Тело может не быть JSON вовсе — например, когда до API не дошли и
    // ответил прокси. Тогда важен код, а не разбор.
    let body: Record<string, unknown> = {}
    if (text !== '') {
      try {
        body = JSON.parse(text) as Record<string, unknown>
      } catch {
        body = { error: text.slice(0, 200) }
      }
    }
    return body as T
  }

  return {
    baseUrl: options.baseUrl,
    tokens: options.tokens,
    request,
    serverNow: () => new Date(Date.now() + skewMs),
  }
}
